import React, { createContext, Dispatch, FC, ReactNode, useContext, useEffect, useState } from 'react';
import { Employee } from '../types';
import Cookies from 'js-cookie';
import { errorToast } from '../../../CommonComponent/toast';
import useFetchWithAuth from '../../../CommonHooks/useFetchWithAuth';

interface ManageEmployeeContextType {
  isDetailReadOnly: boolean;
  setIsDetailsReadOnly: Dispatch<React.SetStateAction<boolean>>;
  filteredList: Employee[];
  setFilteredList: Dispatch<React.SetStateAction<Employee[]>>;
  setInitialData: Dispatch<React.SetStateAction<Employee[] | undefined>>;
  initialData: Employee[] | undefined;
  setIsNew: Dispatch<React.SetStateAction<boolean>>;
  isNew: boolean;
  isLoading: boolean;
  getEmployeeList: () => Promise<void>;
  setIsLoading: Dispatch<React.SetStateAction<boolean>>;
  setSelectedEmployee: Dispatch<React.SetStateAction<Employee | undefined>>;
  selectedEmployee: Employee | undefined;
}

const ManageEmployeeContext = createContext<ManageEmployeeContextType | undefined>(undefined);

const ManageEmployeeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isDetailReadOnly, setIsDetailsReadOnly] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<Employee[]>();
  const [filteredList, setFilteredList] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee>();
  const { fetchWithAuth } = useFetchWithAuth();
  const [isNew, setIsNew] = useState<boolean>(true);
  const userType = Cookies.get('userType');
  const instituteId = Cookies.get('instituteId');

  const getEmployeeList = async () => {
    setIsLoading(true);
    const url = `employee-list/?userType=${userType}&instituteId=${instituteId}&employeeId=&fullName=`;
    const { error, data } = await fetchWithAuth(url);
    if (error) {
      errorToast(error);
      setIsLoading(false);
    } else {
      setInitialData(data);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getEmployeeList();
    // eslint-disable-next-line
  }, [fetchWithAuth, userType, instituteId]);

  return (
    <ManageEmployeeContext.Provider
      value={{
        isDetailReadOnly,
        setIsDetailsReadOnly,
        filteredList,
        setFilteredList,
        initialData,
        setInitialData,
        setIsNew,
        isNew,
        isLoading,
        getEmployeeList,
        setIsLoading,
        setSelectedEmployee,
        selectedEmployee,
      }}
    >
      {children}
    </ManageEmployeeContext.Provider>
  );
};

export const useManageEmployee = (): ManageEmployeeContextType => {
  const context = useContext(ManageEmployeeContext);
  if (!context) {
    throw new Error('useManageEmployee must be used within an ManageEmployeeProvider');
  }
  return context;
};

export default ManageEmployeeProvider;
